export const RESULT_EVENTS = Object.freeze({
  RESULT_TASK_LAYOUT_VIEWED: {
    name: 'result_task_layout_viewed',
    category: 'Result',
    description: 'Result TaskLayout 工作台首次曝光或从其他阶段返回时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_type',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      'subject_id', 'execution_id', 'result_mode'
    ]
  },
  RESULT_TASK_MODULE_VIEWED: {
    name: 'result_task_module_viewed',
    category: 'Result',
    description: 'Result TaskLayout 内部模块、Summary 或 gated placeholder 曝光时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      'subject_id', 'execution_id', 'result_mode', 'feature_code', 'content_mode',
      'compare_view_mode', 'compare_subject'
    ]
  },
  RESULT_TASK_TAB_CHANGED: {
    name: 'result_task_tab_changed',
    category: 'Result',
    description: 'Result TaskLayout 任意稳定 tab 或 segmented control 发生用户切换时触发',
    requiredParams: ['tab_group', 'tab_name'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      'subject_id', 'execution_id', 'result_mode', 'compare_view_mode', 'compare_subject',
      'feature_code', 'content_mode', 'method'
    ]
  },
  RESULT_TASK_COMPARE_VIEW_CHANGED: {
    name: 'result_task_compare_view_changed',
    category: 'Result',
    description: 'Result TaskLayout Dual Compare 的 Single/Dual 视图切换成功或被阻断时触发',
    requiredParams: ['compare_view_mode'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'method', 'tab_group', 'tab_name', 'compare_subject',
      'feature_code', 'content_mode'
    ]
  },
  RESULT_TASK_COMPARE_SUBJECT_CHANGED: {
    name: 'result_task_compare_subject_changed',
    category: 'Result',
    description: 'Result TaskLayout Upload/Reference 主体切换成功、阻断或自动回退时触发',
    requiredParams: ['compare_subject'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'method', 'compare_view_mode', 'tab_group', 'tab_name',
      'feature_code', 'content_mode'
    ]
  },
  RESULT_TASK_OPTION_CHANGED: {
    name: 'result_task_option_changed',
    category: 'Result',
    description: 'Result TaskLayout 配置项、开关或可折叠状态变化时触发',
    requiredParams: ['option_name'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'option_value', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'compare_view_mode', 'compare_subject', 'feature_code',
      'content_mode', 'method'
    ]
  },
  // ══ 阶段 3（2026-10-06）事件级参数下沉 ══════════════════════════════════════════
  // 两类「结构性冗余」的下沉声明，共同语义 = **该键不得进入 payload**：
  //   constants       —— 类型 A：取值由**事件名**唯一决定（逐分支确认过，见方案 §3.6.2）。
  //                      它是注册表元数据而非运行时数据 ⇒ 分析侧用 event_name → 常量映射还原。
  //   redundantParams —— 类型 B：与另一字段**同一表达式**，恒等 ⇒ 只保留后者。值形如
  //                      { 被丢弃的键: 保留的等价键 }，用于 dev 期校验「丢了它，替代键是否在场」。
  // ⚠️ 两条纪律（下沉判错会**静默丢信息**）：
  //   ① 只能在整个事件的所有代码分支都取同一值时下沉（回代码逐分支确认，不能只看数据）；
  //   ② 下沉后必须从 requiredParams/optionalParams **一并移除**，否则「声明峰值」不下降，
  //      静态门（scripts/check-analytics-param-budget.js）会因此低估真实占用。
  // 运行时由 `buildAnalyticsPayload` 按声明强制剥离（防回归）+ dev 失配告警。
  // 分析侧还原映射（附录 A5）：`SELECT event_name, CASE ... END AS <key>` 或一张维度表。
  // ══════════════════════════════════════════════════════════════════════════════
  RESULT_TASK_MASK_CONTROL_CHANGED: {
    name: 'result_task_mask_control_changed',
    category: 'Result',
    description: 'Result TaskLayout mask tab 或 Color Grading 下 zone/channel/slider 等深层控制变化时触发',
    // 逐分支确认（2026-10-06）：全部 5 条 emit 路径（MaskComparePanel 通道/区域/滑杆、
    // ColorGrading zone、zone-click）都无 auto/programmatic 分支 ⇒ method 恒为 user_click。
    // ⚠️ 本事件 own 26 → 23，声明峰值 29 → 26 **仍 > 25** ⇒ 未脱离「可能触顶」（已如实记录）。
    constants: {
      event_label: 'mask_control_changed',
      module_name: 'result',
      method: 'user_click'
    },
    requiredParams: [],
    optionalParams: [
      'source_position', 'element_name', 'element_type',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      'subject_id', 'execution_id', 'result_mode', 'compare_view_mode',
      'compare_subject', 'tab_group', 'tab_name', 'mask_zone', 'mask_channel',
      'slider_mode', 'option_name', 'option_value', 'action_status', 'reason_code',
      'feature_code', 'content_mode'
    ]
  },
  // ⚠️ 2026-09-29 审计删除 RESULT_TASK_EVIDENCE_OPENED('result_task_evidence_opened')：
  // 自注册以来零调用点，对应的「gap evidence / 原子能力锚点」交互在当前 UI 中不存在；
  // 相邻跳转的曝光与点击已由 result_task_module_viewed / result_task_summary_* 承载。
  // 若该功能回归，须连同调用点一起恢复，勿只恢复注册表。
  RESULT_TASK_ASSET_DOWNLOAD_COMPLETED: {
    name: 'result_task_asset_download_completed',
    category: 'Result',
    description: 'Result TaskLayout 图片素材下载成功、失败或阻断时触发',
    requiredParams: ['action_status', 'download_kind'],
    // ⚠️ duration_ms 的语义 = 「下载准备耗时」，**只在确实存在异步准备阶段的路径产出**：
    // reference_image 走跨域 fetch + blob 化，是真实网络等待 ⇒ 上报；
    // original_image / processed_image 是同步 <a>.click() ⇒ 整键省略，绝不发 ~0 的假时长（0 会拉低均值）。
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'reason_code', 'content_id', 'content_type', 'related_content_id',
      'related_content_type', 'subject_id', 'execution_id', 'result_mode',
      'compare_view_mode', 'compare_subject', 'feature_code', 'content_mode',
      'method', 'export_format', 'duration_ms'
    ]
  },
  RESULT_TASK_DATA_EXPORT_COMPLETED: {
    name: 'result_task_data_export_completed',
    category: 'Result',
    description: 'Result TaskLayout JSON 或统计数据导出成功、失败或阻断时触发',
    requiredParams: ['action_status', 'download_kind'],
    // ⚠️ 刻意**不声明** duration_ms：该事件的全部路径都是同步的
    // （JSON.stringify → Blob → <a>.click()，见 GapCardsPanel.downloadActiveJson），
    // 没有任何可测的异步跨度。声明了只会永远缺席（R-8），因此从注册表移除。
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'reason_code', 'content_id', 'content_type', 'related_content_id',
      'related_content_type', 'subject_id', 'execution_id', 'result_mode',
      'tab_group', 'tab_name', 'mask_zone', 'compare_view_mode', 'compare_subject',
      'feature_code', 'content_mode', 'export_format', 'method'
    ]
  },
  RESULT_TASK_REFERENCE_LOAD_COMPLETED: {
    name: 'result_task_reference_load_completed',
    category: 'Result',
    description: 'Result TaskLayout reference 预览加载探测或 retry 完成时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'reason_code', 'content_id', 'content_type', 'related_content_id',
      'related_content_type', 'subject_id', 'execution_id', 'result_mode',
      'compare_view_mode', 'compare_subject', 'feature_code', 'content_mode',
      'method', 'duration_ms'
    ]
  },
  RESULT_TASK_SUMMARY_CONTENT_VIEWED: {
    name: 'result_task_summary_content_viewed',
    category: 'Result',
    description: 'Result TaskLayout Summary 内部区域内容曝光时触发',
    requiredParams: ['summary_section', 'content_mode'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'feature_code', 'render_variant', 'item_count', 'has_content',
      'is_ai_upgrade_visible', 'is_ai_upgrade_in_progress', 'ai_upgrade_state'
    ]
  },
  RESULT_TASK_SUMMARY_DIALOG_TOGGLED: {
    name: 'result_task_summary_dialog_toggled',
    category: 'Result',
    description: 'Result TaskLayout Summary AI detail 弹窗打开或关闭时触发',
    requiredParams: ['dialog_state'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'option_name', 'option_value', 'summary_section', 'summary_action_type',
      'open_trigger', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'render_variant', 'has_content'
    ]
  },
  RESULT_TASK_SUMMARY_ACTION_CLICKED: {
    name: 'result_task_summary_action_clicked',
    category: 'Result',
    description: 'Result TaskLayout Summary 普通 CTA 点击时触发',
    requiredParams: ['summary_action_type'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'summary_section', 'content_mode', 'has_content'
    ]
  },
  RESULT_TASK_SUMMARY_AI_UPGRADE_CLICKED: {
    name: 'result_task_summary_ai_upgrade_clicked',
    category: 'Result',
    description: 'Result TaskLayout Summary AI 升级 CTA 点击时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'summary_section', 'summary_action_type', 'feature_code',
      'content_mode', 'is_ai_upgrade_visible', 'is_ai_upgrade_in_progress', 'ai_upgrade_state'
    ]
  },

  RESULT_GUIDE_ENTRY_CLICKED: {
    name: 'result_guide_entry_clicked',
    category: 'Result',
    description: 'Result Guide 入口被用户点击时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'content_id', 'content_type', 'related_content_id', 'related_content_type',
      'subject_id', 'execution_id', 'result_mode', 'guide_launch_source'
    ]
  },
  RESULT_GUIDE_ENTRY_RESOLVED: {
    name: 'result_guide_entry_resolved',
    category: 'Result',
    description: 'Result Guide 入口判定完成、进入或跳过时触发',
    requiredParams: ['action_status', 'guide_entry_state'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'reason_code', 'content_id', 'content_type', 'related_content_id',
      'related_content_type', 'subject_id', 'execution_id', 'result_mode',
      'guide_launch_source', 'guide_step_count', 'auto_guide_seen_before'
    ]
  },
  RESULT_GUIDE_LAYOUT_VIEWED: {
    name: 'result_guide_layout_viewed',
    category: 'Result',
    description: 'Result GuideLayout 首次可见或重新进入时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      'subject_id', 'execution_id', 'result_mode', 'guide_launch_source',
      'guide_step_count'
    ]
  },
  RESULT_GUIDE_STEP_VIEWED: {
    name: 'result_guide_step_viewed',
    category: 'Result',
    description: 'Result Guide 中某个步骤首次可见时触发',
    // 逐分支确认（2026-10-06）：唯一入口 useResultGuideFlow.js:457-471（trackGuideStepViewedOnce），
    // 传入的是 normalizeGuideStepAnalytics 产物 + 上下文，**不含** eventLabel/sourcePosition/
    // elementName/elementType ⇒ 这 5 个按键取值全部恒定（element_name 恒等于 guide_step_type）。
    // ⚠️ feature_code 由 normalizeGuideStepAnalytics:420 写死 'result_guide' ⇒ 同为常量。
    constants: {
      event_label: 'step_viewed',
      module_name: 'result',
      element_type: 'step',
      source_position: 'result_guide_step_card',
      feature_code: 'result_guide'
    },
    // element_name 恒等于 guide_step_type：hook 里 element_name 取 `payload.elementName || payload.guideStepType`，
    // 而唯一调用点从不传 elementName ⇒ 两个名字同一个值，只保留 guide_step_type（与 step_viewed/interaction 命名一致）。
    redundantParams: {
      element_name: 'guide_step_type'
    },
    requiredParams: ['guide_step_id', 'guide_step_type', 'guide_step_phase', 'guide_step_index'],
    optionalParams: [
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      'subject_id', 'execution_id', 'result_mode', 'guide_launch_source',
      'guide_step_count', 'guide_progress_depth', 'content_mode',
      'guide_sub_feature_code', 'reason_code', 'guide_gap_id', 'guide_gap_index',
      'guide_gap_severity', 'guide_sub_step_type'
    ]
  },
  RESULT_GUIDE_STEP_CHANGED: {
    name: 'result_guide_step_changed',
    category: 'Result',
    description: 'Result Guide 步骤导航成功或被阻断时触发',
    // 逐分支确认（2026-10-06）：5 条 emit 分支（next/prev 成功、跳转非法、跳转被阻、跳转成功）
    // 都不传 eventLabel/sourcePosition；module_name 由 withResultGuideDefaults 硬编码；
    // feature_code 由 normalizeGuideStepAnalytics:420 写死 ⇒ 4 个常量下沉。
    constants: {
      event_label: 'step_changed',
      module_name: 'result',
      source_position: 'result_guide_progress',
      // ⚠️ feature_code 原本**未声明却实发**（normalizeGuideStepAnalytics 产物经 withGuideStepFields 注入）
      // ⇒ 此处下沉是**真实省 1 slot**，但因它不在声明里，声明峰值不变（口径缺口见方案 §3.6.4 待办）。
      feature_code: 'result_guide'
    },
    // 「to 侧」与 guide_step_* 是**同一表达式**（useResultGuideFlow.js:711/736/789/813 传 toStep?.id、
    // toStepIndex，紧邻的 ...normalizeGuideStepAnalytics(toStep, toStepIndex) 产出 guideStepId/guideStepIndex）
    // ⇒ 只保留 guide_* 一套；to_step_* 从调用点已删（本声明是防回归兜底）。
    // ⚠️ 唯一不等价分支：跳转被阻且 targetStep 越界为 null 时 to_step_id 缺席，而 guide_step_id
    //    回落到 fromStep.id（useResultGuideFlow.js:791 的 `targetStep || fromStep`）—— 该分支下
    //    guide_step_id 语义本就是 from（既有行为），下沉不新增损失。
    redundantParams: {
      to_step_id: 'guide_step_id',
      to_step_index: 'guide_step_index'
    },
    // to_step_index 移除后，补声明 guide_step_index —— 它由 normalizeGuideStepAnalytics:417 恒定产出并被
    // 声明为 required 的兄弟事件一致对待，此前在 step_changed 上属「实发未声明」。
    requiredParams: ['action_status', 'from_step_index'],
    optionalParams: [
      'element_name', 'element_type',
      'method', 'reason_code', 'content_id', 'content_type', 'related_content_id',
      'related_content_type', 'subject_id', 'execution_id', 'result_mode',
      'guide_launch_source', 'guide_step_count', 'from_step_id',
      'guide_step_id', 'guide_step_type', 'guide_step_phase', 'guide_progress_depth',
      'guide_step_index'
    ]
  },
  RESULT_GUIDE_STEP_INTERACTION: {
    name: 'result_guide_step_interaction',
    category: 'Result',
    description: 'Result Guide 步骤内部低频稳定交互时触发',
    // 逐分支确认（2026-10-06）：唯一 emit 者 MaskOverviewStep / StructuredGapRenderer 均不传
    // eventLabel / featureCode（后者由 normalizeGuideStepAnalytics:420 写死）⇒ 3 个常量下沉。
    // ⚠️ 本事件 own 29 → 26、声明峰值 32 → 29 **仍 > 25**，是本批最大的重事件，**未脱离「可能触顶」**；
    //    靠常量下沉无法达标（它的多值字段全是真信息），需后续拆事件，已登记待办。
    constants: {
      event_label: 'step_interaction',
      module_name: 'result',
      feature_code: 'result_guide'
    },
    requiredParams: ['guide_step_id', 'guide_step_type'],
    optionalParams: [
      'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'guide_launch_source', 'guide_step_phase', 'guide_step_index',
      'guide_step_count', 'content_mode', 'guide_sub_feature_code',
      'guide_gap_id', 'guide_gap_index', 'guide_sub_step_type', 'option_name', 'option_value'
    ]
  },
  RESULT_GUIDE_AI_GATE_COMPLETED: {
    name: 'result_guide_ai_gate_completed',
    category: 'Result',
    description: 'Result Guide AI Gate 等待完成、超时、跳过或取消时触发',
    requiredParams: ['action_status', 'guide_ai_gate_state'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'reason_code',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      'subject_id', 'execution_id', 'result_mode', 'guide_launch_source',
      'guide_ai_runtime_state', 'duration_ms'
    ]
  },
  RESULT_GUIDE_COMPLETED: {
    name: 'result_guide_completed',
    category: 'Result',
    description: 'Result Guide 被完成或系统回退时触发',
    requiredParams: ['guide_completion_state'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'guide_launch_source', 'guide_step_id', 'guide_step_index',
      'guide_step_count', 'duration_ms'
    ]
  },
  RESULT_GUIDE_EXITED: {
    name: 'result_guide_exited',
    category: 'Result',
    description: 'Result Guide 被主动退出或中断时触发',
    requiredParams: ['guide_completion_state'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'subject_id', 'execution_id',
      'result_mode', 'guide_launch_source', 'guide_step_id', 'guide_step_index',
      'guide_step_count', 'duration_ms'
    ]
  },

  RESULT_PENDING_ENTERED: {
    name: 'result_pending_entered',
    category: 'Result',
    description: 'Result 页面进入 upload pending 等待态时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'route_kind', 'result_mode',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      // reference_code 仅在与 preset_style_id **不同**时才发：上游无 reference_code 时两者必然同值
      // （uploadAnalysisQuoteGateway.js:56 末级 fallback）⇒ 缺席即代表两者相等，BQ 用 COALESCE 可还原。
      'preset_style_id', 'reference_code', 'request_id'
    ]
  },
  RESULT_PENDING_CONTEXT_RESOLVED: {
    name: 'result_pending_context_resolved',
    category: 'Result',
    description: 'Result pending context 匹配、恢复、重建或缺失后触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'reason_code', 'request_id',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      // reference_code 仅在与 preset_style_id **不同**时才发：上游无 reference_code 时两者必然同值
      // （uploadAnalysisQuoteGateway.js:56 末级 fallback）⇒ 缺席即代表两者相等，BQ 用 COALESCE 可还原。
      'preset_style_id', 'reference_code', 'subject_id', 'execution_id'
    ]
  },
  RESULT_PENDING_LAYOUT_VIEWED: {
    name: 'result_pending_layout_viewed',
    category: 'Result',
    description: 'Result pending layout 曝光或关键展示状态变化时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_type', 'execution_state',
      'pending_step', 'request_id', 'content_id', 'content_type', 'reason_code',
      'recovery_type', 'next_action_type'
    ]
  },
  RESULT_PENDING_READY_STATE_CHANGED: {
    name: 'result_pending_ready_state_changed',
    category: 'Result',
    description: 'Result pending 等待 readyForExecution 过程中的关键状态变化',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'pending_step', 'execution_state',
      'request_id', 'content_id', 'content_type', 'cropping_detected', 'is_auto_run',
      'reason_code'
    ]
  },
  RESULT_PENDING_EXECUTION_STARTED: {
    name: 'result_pending_execution_started',
    category: 'Result',
    description: 'Result pending 自动或手动开始执行 runs 前触发',
    requiredParams: ['request_id'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'service', 'operation_type',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      // reference_code 仅在与 preset_style_id **不同**时才发：上游无 reference_code 时两者必然同值
      // （uploadAnalysisQuoteGateway.js:56 末级 fallback）⇒ 缺席即代表两者相等，BQ 用 COALESCE 可还原。
      'preset_style_id', 'reference_code', 'is_auto_run', 'retry_count',
      // 0.9.6.7：自 pending 进入到「开始执行」的总耗时。用于回答
      // 「用户等了多久才开跑」，并与 result_pending_heartbeat 序列交叉校验
      // （心跳停了但 execution_started 才到 ⇒ 说明用户早就离开了）。
      'bootstrap_total_duration_ms',
      // 0.9.6.8：开跑那一刻的可见性。此前无法从数据判断"这次执行是不是在后台启动的"
      // （只能靠 execution_started 时间与心跳缺失区间做区间推断）。
      'visibility_state', 'hidden_accumulated_ms'
    ]
  },
  RESULT_PENDING_EXECUTION_STAGE: {
    name: 'result_pending_execution_stage',
    category: 'Result',
    description: 'Result pending 执行链路阶段推进（resolve_assets / resolve_uploads / create_run / watchdog_timeout），用于定位执行挂起的具体环节',
    requiredParams: ['pending_stage'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'service', 'operation_type',
      // ⚠️ preset_style_id / reference_code 当前**从未被 emit**（trackResultPendingExecutionStage
      // 不转发 presetStyleId/referenceCode）⇒ 此处声明仅为保守占位，实际不占用参数槽。
      'request_id', 'content_id', 'content_type', 'preset_style_id', 'reference_code',
      'is_auto_run', 'action_status', 'reason_code', 'duration_ms',
      // 0.9.6.8：看门狗时长拆解。duration_ms 保持「墙钟」语义（既有反推公式
      // exec_start = ts − duration_ms 依赖它，不能改），另给 hidden_ms / active_ms，
      // 使「180s 里有多少是被冻结吃掉的」可直接读出，而不是事后靠迟到量反推。
      'visibility_state', 'hidden_ms', 'active_ms'
    ]
  },
  RESULT_PENDING_ASSET_UPLOAD_COMPLETED: {
    name: 'result_pending_asset_upload_completed',
    category: 'Result',
    description: 'Result pending 阶段 run asset 补传成功或失败时触发',
    requiredParams: ['action_status', 'asset_kind'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'service', 'operation_type',
      'request_id', 'content_id', 'content_type', 'reason_code', 'http_status',
      'mime_type', 'duration_ms'
    ]
  },
  RESULT_PENDING_EXECUTION_COMPLETED: {
    name: 'result_pending_execution_completed',
    category: 'Result',
    description: 'Result pending runs 执行完成、失败或异常时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'service', 'operation_type',
      'request_id', 'subject_id', 'execution_id', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'reason_code', 'http_status',
      'duration_ms', 'retry_count', 'next_action_type', 'recovery_type'
    ]
  },
  RESULT_PENDING_RETRY_CLICKED: {
    name: 'result_pending_retry_clicked',
    category: 'Result',
    description: '用户点击 Result pending retry 按钮时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'request_id', 'subject_id', 'execution_id', 'content_id', 'content_type',
      'reason_code', 'recovery_type', 'next_action_type'
    ]
  },
  RESULT_PENDING_RETRY_COMPLETED: {
    name: 'result_pending_retry_completed',
    category: 'Result',
    description: 'Result pending 人工重试完成、阻断或失败时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'service', 'operation_type',
      'request_id', 'subject_id', 'execution_id', 'content_id', 'content_type',
      'reason_code', 'http_status', 'retry_count', 'recovery_type',
      // 0.9.6.8：重试自身的耗时（墙钟，从点击重试到拿到终态）。
      // 用于回答「重试也要等这么久吗」；看门狗代报时它等于执行预算，
      // 与 reason_code=pending_execution_watchdog_timeout 一起读即可区分「真失败 vs 从未 settle」。
      'duration_ms'
    ]
  },
  RESULT_PENDING_SUBJECT_REFRESH_COMPLETED: {
    name: 'result_pending_subject_refresh_completed',
    category: 'Result',
    description: 'Result pending runs 成功后 subject 回刷完成或失败时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'service', 'operation_type',
      'request_id', 'subject_id', 'execution_id', 'reason_code', 'flushed_count',
      'duration_ms'
    ]
  },
  RESULT_PENDING_CANONICALIZE_COMPLETED: {
    name: 'result_pending_canonicalize_completed',
    category: 'Result',
    description: 'Result pending hash route canonicalize 到 subject route 完成、失败或跳过时触发',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'service', 'operation_type',
      'request_id', 'subject_id', 'execution_id', 'route_kind', 'reason_code',
      'duration_ms'
    ]
  },
  RESULT_PENDING_EXITED: {
    name: 'result_pending_exited',
    category: 'Result',
    description: 'Result pending 状态退出或终止时触发（含路由离开、组件卸载、关闭/隐藏页面）',
    requiredParams: ['action_status'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'request_id', 'subject_id',
      'execution_id', 'content_id', 'content_type', 'reason_code', 'duration_ms',
      'recovery_type',
      // 0.9.6.7：区分「用户主动离开」与「流程侧退出」。Android 上 beforeunload
      // 经常不触发，visibility_hidden / pagehide 才是可靠的离开信号。
      'exit_trigger',
      // 0.9.6.8：门闩改语义。此前的 `if (!shouldShowPendingLayout) return` 让「非
      // reference_task / 历史图入口 / 已成功」时**整条不发**，导致 Result 页的
      // 「用户离开」几乎测不到（10-01 起全站 visibility_hidden 仅 1 条）。现在
      // 一律发送，是否处于 pending 只作为**字段**表达，供报表侧过滤。
      'pending_active', 'should_show_pending_layout', 'visibility_state'
    ]
  },
  // ── 0.9.6.7 执行前链路可观测性（线上日志判定用） ──
  // 背景：线上事故「点开始分析 → 无后续 → 未扣积分」时，日志只能定位到
  // result_pending_layout_viewed，无法判断卡在 bootstrap 哪一段、更无法判断
  // 用户是否已离开（两者日志同形）。以下 3 条事件专用于打破该对称。
  RESULT_PENDING_BOOTSTRAP_STAGE: {
    name: 'result_pending_bootstrap_stage',
    category: 'Result',
    description: 'Result pending 执行前 bootstrap 阶段推进（组件挂载 / worker 预载 / OpenCV 就绪 / 图片加载 / 派发 / worker 首消息 / 裁切决策 / ready）',
    requiredParams: ['bootstrap_stage'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'action_status', 'reason_code',
      'duration_ms', 'total_duration_ms', 'pending_step', 'request_id',
      'content_id', 'content_type', 'cropping_detected', 'processed_ready',
      'ready_for_execution'
    ]
  },
  RESULT_PENDING_PROCESSOR_SKIPPED: {
    name: 'result_pending_processor_skipped',
    category: 'Result',
    description: 'Result 页面未挂载 AnalysisProcessor（未满足 shouldMountAnalysisProcessor）时触发；该分支下分析子系统整体静默，必须由 ResultView 补发',
    requiredParams: ['reason_code'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'request_id',
      'content_id', 'content_type', 'result_mode', 'has_image_data', 'has_image_url'
    ]
  },
  RESULT_PENDING_HEARTBEAT: {
    name: 'result_pending_heartbeat',
    category: 'Result',
    description: 'Result pending 停留期间的存活性探针（15s 一次、最多 8 次、仅页面可见时）；末条心跳的 elapsed_ms 即「用户还在页面的最后时刻」',
    requiredParams: ['elapsed_ms'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'bootstrap_stage', 'pending_step',
      'ready_for_execution', 'execution_state', 'request_id', 'content_id', 'content_type',
      // 0.9.6.8：显式可见性字段。此前心跳「缺席」只暗示 hidden（回调内有 hidden 早退），
      // 无法区分「页面从未切走」与「切走了但这一拍被门闩吞掉」。补上后可直接观测。
      'visibility_state', 'hidden_gap_ms', 'hidden_accumulated_ms'
    ]
  },

  // ── 0.9.6.8 pending 可见性可观测性 ──
  // 背景（2026-10-05 事故）：pending 执行在页面已转后台后才自动启动，上传被冻结打断，
  // 看门狗把冻结时间算进 180s 预算 ⇒ 切后台回前台必失败。事后排查时 pending 侧
  // **没有任何可见性字段**，"是否切过后台"只能靠心跳缺席 + 定时器迟到量间接推断。
  // 以下两条事件把该现象变成可直接读取的事实。
  RESULT_PENDING_VISIBILITY_CHANGED: {
    name: 'result_pending_visibility_changed',
    category: 'Result',
    description: 'Result 页面可见性发生变化（visible/hidden 双向都发）；用于把「用户是否切过后台、切了多久」从推断变成观测。刻意不带业务门闩：只要有变化就发。',
    requiredParams: ['visibility_state'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'request_id', 'content_id',
      'content_type', 'pending_active', 'should_show_pending_layout', 'execution_state',
      'hidden_gap_ms', 'hidden_transition_count', 'heartbeat_count', 'visibility_state'
    ]
  },
  RESULT_PENDING_FAILURE_SHOWN: {
    name: 'result_pending_failure_shown',
    category: 'Result',
    description: 'Result pending 失败提示（错误文案或重试按钮）实际呈现给用户时触发；由 UI 渲染驱动，不依赖执行 promise 是否 settle（挂死/冻结/被杀时 retry_completed 永不发出，本条仍会发）',
    requiredParams: ['display_variant'],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'request_id', 'content_id',
      'content_type', 'reason_code', 'recovery_type', 'next_action_type',
      'display_variant', 'retryable', 'visibility_state', 'duration_ms'
    ]
  },

  RESULT_FINAL_REVIEW_ENTRY_CLICKED: {
    name: 'result_final_review_entry_clicked',
    category: 'Result',
    description: '用户在 ResultTaskLayout 点击进入 FinalReview / upload result 入口时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type', 'result_mode'
    ]
  },
  RESULT_FINAL_REVIEW_PANEL_OPENED: {
    name: 'result_final_review_panel_opened',
    category: 'Result',
    description: 'openUploadResult 或路由恢复使 resultViewMode=final_review 时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type',
      // R-7：调用方一直在传 resultMode，此前注册表未声明且 hook 未映射（双重缺失）。
      'result_mode'
    ]
  },
  RESULT_FINAL_REVIEW_RESTORE_COMPLETED: {
    name: 'result_final_review_restore_completed',
    category: 'Result',
    description: 'restoreFinalReviewFromRoutePanel 完成缓存恢复或跳过/失败时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'method', 'action_status',
      'reason_code', 'content_id', 'content_type', 'related_content_id',
      'related_content_type', 'review_state',
      // R-7：同上。
      'result_mode'
    ]
  },
  RESULT_FINAL_REVIEW_LAYOUT_VIEWED: {
    name: 'result_final_review_layout_viewed',
    category: 'Result',
    description: 'ResultFinalReviewLayout 首次曝光或 upload/review 状态首次曝光时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_type',
      'content_id', 'content_type', 'related_content_id', 'related_content_type',
      'image_id', 'review_state'
    ]
  },
  RESULT_FINAL_REVIEW_UPLOAD_STARTED: {
    name: 'result_final_review_upload_started',
    category: 'Result',
    description: '用户选择/拖拽完成图并通过校验后开始处理时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'content_id', 'content_type', 'related_content_id',
      'related_content_type', 'image_id', 'mime_type', 'file_size_bucket'
    ]
  },
  RESULT_FINAL_REVIEW_UPLOAD_COMPLETED: {
    name: 'result_final_review_upload_completed',
    category: 'Result',
    description: '完成图 palette 提取、store 写入、缓存保存成功或失败时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'image_id', 'mime_type',
      'file_size_bucket', 'duration_ms', 'error_name'
    ]
  },
  RESULT_FINAL_REVIEW_PREVIEW_CHANGED: {
    name: 'result_final_review_preview_changed',
    category: 'Result',
    description: '用户切换 slider / sideBySide 预览模式时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'preview_mode', 'template_id', 'template_layout'
    ]
  },
  RESULT_FINAL_REVIEW_TEMPLATE_SELECTED: {
    name: 'result_final_review_template_selected',
    category: 'Result',
    description: '用户选择分享模板时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'preview_mode', 'template_id', 'template_layout'
    ]
  },
  RESULT_FINAL_REVIEW_OPTION_CHANGED: {
    name: 'result_final_review_option_changed',
    category: 'Result',
    description: 'MoodFrame 或 Before 来源开关变化时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'option_name', 'option_value'
    ]
  },
  RESULT_FINAL_REVIEW_SHARE_EXPORT_STARTED: {
    name: 'result_final_review_share_export_started',
    category: 'Result',
    description: '用户点击下载分享图且开始 html-to-image 生成时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'content_id', 'content_type', 'related_content_id',
      'related_content_type', 'image_id', 'export_format', 'export_scale',
      'has_before_image', 'has_after_image', 'uses_mood_frame', 'uses_border_cropped_before',
      'preview_mode', 'template_id', 'template_layout'
    ]
  },
  RESULT_FINAL_REVIEW_SHARE_EXPORT_COMPLETED: {
    name: 'result_final_review_share_export_completed',
    category: 'Result',
    description: '分享图生成下载成功、失败或被阻断时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'image_id', 'export_format',
      'export_scale', 'has_before_image', 'has_after_image', 'uses_mood_frame',
      'uses_border_cropped_before', 'duration_ms',
      'preview_mode', 'template_id', 'template_layout'
    ]
  },
  RESULT_FINAL_REVIEW_BACK_CLICKED: {
    name: 'result_final_review_back_clicked',
    category: 'Result',
    description: '用户点击 Back to task 时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'content_id', 'content_type', 'related_content_id', 'related_content_type'
    ]
  },
  RESULT_FINAL_REVIEW_EXITED: {
    name: 'result_final_review_exited',
    category: 'Result',
    description: 'FinalReview 退出到 tasklayout、路由变化或非 reference_task 强制退出时触发',
    requiredParams: [],
    optionalParams: [
      'event_label', 'module_name', 'source_position', 'element_name', 'element_type',
      'method', 'action_status', 'reason_code', 'content_id', 'content_type',
      'related_content_id', 'related_content_type', 'duration_ms'
    ]
  }
});
